import { Controller } from '@hotwired/stimulus'
import { alerting } from '../lib/alert_modal'
import { FileUploader } from '../lib/file_uploader'
import { FileValidator } from '../lib/file_validator'
import { FilePreview } from '../lib/file_preview'
import { post, put } from '../lib/request'

const URL = '/admin/products'
const UPLOAD_TIMEOUT = 120000 // 2 minutes timeout

export default class extends Controller {
  static values = {
    validType: { type: String, default: 'image/*' },
    validSize: { type: Number, default: 100 },
    maxLength: { type: Number, default: 30 },
    uploadLimit: { type: Number, default: 100 },
  }

  connect = () => {
    this.files = []
    this.activeUploads = 0
    this.addListeners()

    // Set up periodic cleanup for stalled uploads
    this.cleanupInterval = setInterval(this.checkForStalledUploads, 30000)

    window.addEventListener('beforeunload', this.handleBeforeUnload)
  }

  disconnect = () => {
    this.removeAllListeners()
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    clearInterval(this.cleanupInterval)
  }

  addListeners = () => {
    if (this.uploadButton) {
      this.uploadButton.addEventListener('click', this.openFileChooser)
    }

    if (this.mobileUploadButton) {
      this.mobileUploadButton.addEventListener('click', this.openFileChooser)
    }

    this.element.addEventListener('change', this.fileManuallyAdded)

    this.dragCounter = 0
    document.addEventListener('dragover', event => event.preventDefault())
    document.addEventListener('dragenter', this.handleDragEnter)
    document.addEventListener('dragleave', this.handleDragLeave)
    document.addEventListener('drop', this.handleFileDrop)
  }

  removeAllListeners = () => {
    if (this.uploadButton) {
      this.uploadButton.removeEventListener('click', this.openFileChooser)
    }
    if (this.mobileUploadButton) {
      this.mobileUploadButton.removeEventListener('click', this.openFileChooser)
    }
    this.element.removeEventListener('change', this.fileManuallyAdded)

    document.removeEventListener('dragenter', this.handleDragEnter)
    document.removeEventListener('dragleave', this.handleDragLeave)
    document.removeEventListener('drop', this.handleFileDrop)
  }

  handleBeforeUnload = (event) => {
    if (this.activeUploads > 0) {
      const message = 'You have uploads in progress. If you leave now, they may be lost.'
      event.preventDefault()
      event.returnValue = message
      return message
    }
  }

  handleDragEnter = (event) => {
    event.preventDefault()
    this.dragCounter++

    // Only show dropOver when we start dragging (counter goes from 0 to 1)
    if (this.dragCounter === 1 && this.dropOver) {
      this.dropOver.classList.remove('hidden')
    }
  }

  handleDragLeave = () => {
    this.dragCounter--

    // Only hide dropOver when we completely leave the drag area (counter reaches 0)
    if (this.dragCounter <= 0 && this.dropOver) {
      this.dropOver.classList.add('hidden')
      this.dragCounter = 0 // Reset to prevent negative values
    }
  }

  handleFileDrop = (event) => {
    event.preventDefault()
    this.fileAdded(event.dataTransfer.files)

    // Reset counter and hide dropOver after drop
    this.dragCounter = 0
    if (this.dropOver) {
      this.dropOver.classList.add('hidden')
    }
  }

  fileManuallyAdded = () => {
    this.fileAdded(this.element.files)
  }

  fileAdded = (files) => {
    // Check against total upload limit including current uploads
    if (this.activeUploads + files.length > this.uploadLimitValue) {
      return this.uploadLimitReached()
    }

    if (files.length > this.maxLengthValue) {
      return this.alertTooManyFiles()
    }

    Array.from(files).forEach(file => this.processFile(file))
  }

  processFile = async (file) => {
    if (this.fileExists(file)) {
      return alerting('File already uploaded.')
    }

    // Create file tracking object
    const fileData = {
      file: file,
      uuid: this.generateUUID(),
      status: 'validating',
      progress: 0,
      added: new Date(),
      lastProgressUpdate: new Date(),
    }

    // Add to tracking
    this.files.push(fileData)

    // Create UI element for this file
    this.createFileUI(fileData)

    try {
      // Validate file
      const validator = new FileValidator(file, this.fileOptions)
      const isValid = await validator.validate()

      if (!isValid) {
        this.handleFileError(fileData, new Error('File validation failed'))
        return
      }

      // Generate preview
      await this.generatePreview(fileData)

      // Start upload
      fileData.status = 'pending'
      this.activeUploads++
      this.updateFileUI(fileData)

      await this.startUpload(fileData)
    }
    catch (error) {
      console.error('File processing error:', error)
      this.handleFileError(fileData, error)
    }
  }

  generatePreview = async (fileData) => {
    try {
      const preview = new FilePreview(fileData.file)
      const previewData = await preview.generate()

      fileData.preview = previewData
      this.updateFilePreview(fileData)
    }
    catch (error) {
      console.warn('Preview generation failed:', error)
      // Continue without preview - not a fatal error
    }
  }

  startUpload = async (fileData) => {
    try {
      // First create the product record
      const productData = await this.createProduct(fileData)

      fileData.created = true
      fileData.productId = productData.id
      fileData.status = 'uploading'

      // Update element ID to use product ID
      fileData.elements.forEach((element) => {
        element.classList.add(`print_product_${productData.id}`)
      })

      this.updateFileUI(fileData)

      // Start the actual file upload with callbacks
      const uploader = new FileUploader(fileData.file, this.element.dataset.directUploadUrl)
      fileData.uploader = uploader

      await uploader.upload({
        onProgress: progress => this.handleUploadProgress(fileData, progress),
        onComplete: blob => this.handleUploadComplete(fileData, blob),
        onError: error => this.handleFileError(fileData, error),
      })
    }
    catch (error) {
      this.handleFileError(fileData, error)
    }
  }

  handleUploadProgress = (fileData, progress) => {
    fileData.progress = progress
    fileData.lastProgressUpdate = new Date()
    this.updateFileUI(fileData)
  }

  handleUploadComplete = async (fileData, blob) => {
    fileData.status = 'complete'
    fileData.signedId = blob.signed_id
    this.activeUploads--

    this.updateFileUI(fileData)

    // Update the product with the asset
    if (fileData.productId) {
      try {
        await this.updateProduct(fileData.productId, blob.signed_id)
      }
      catch (error) {
        console.error('Error updating product with asset:', error)
      }
    }
  }

  updateProduct = async (productId, signedId) => {
    const params = {
      print_product: {
        asset: signedId,
      },
    }

    return await put(`${URL}/${productId}`, {
      responseKind: 'json',
      body: JSON.stringify(params),
    })
  }

  handleFileError = (fileData, error) => {
    console.error('Upload error for file:', fileData.file.name, error)

    fileData.status = 'error'
    fileData.error = error?.message || 'Unknown error'
    this.activeUploads--

    this.updateFileUI(fileData)

    // Notify user
    alerting(`Upload failed for "${fileData.file.name}": ${fileData.error}`)

    // Keep file in UI - removed removeFileUI call
  }

  createFileUI = (fileData) => {
    // Initialize elements array if it doesn't exist
    if (!fileData.elements) {
      fileData.elements = []
    }

    this.uploadsContainers.forEach((container) => {
      if (!container) return
      const fileElement = document.createElement('div')
      fileElement.className = `default-border-left bg-gray-200 border-b border-gray-300 flex px-1 w-screen md:w-full cursor-progress ${fileData.uuid}`
      fileElement.dataset.fileId = fileData.uuid
      fileElement.innerHTML = `
        <div class="flex w-full justify-between gap-4 items-center h-16">
          <div class="size-12 md:ml-4 center-x-y relative" data-upload-image-container="true">
            <div class="w-full h-full flex items-center justify-center bg-gray-100 rounded">
              <div class="text-xs text-gray-600">Loading...</div>
            </div>
          </div>
          <div class="grow flex flex-col gap-1 items-start">
            <div class="text-xs truncate">${fileData.file.name}</div>
            <div class="w-full">
              <div class="border-2 border-gray-600 rounded-full">
                <div class="bg-gray-600 progressbar h-1 transition-all duration-300" style="width: ${fileData.progress}%"></div>
              </div>
            </div>
            <div class="text-xs">
              <span class="upload-status-text">${this.getStatusText(fileData.status)}</span>
            </div>
          </div>
          <div class="flex justify-end md:mr-4">
            <div class="status-icon">
              ${this.getStatusIcon(fileData.status)}
            </div>
          </div>
        </div>
      `
      // Add to top of container instead of bottom
      container.insertBefore(fileElement, container.firstChild)

      // Push element to array instead of overwriting single element
      fileData.elements.push(fileElement)
    })
  }

  updateFileUI = (fileData) => {
    if (!fileData.elements) return

    fileData.elements.forEach((element) => {
      const statusText = element.querySelector('.upload-status-text')
      const progressBar = element.querySelector('.progressbar')
      const statusIcon = element.querySelector('.status-icon')

      if (statusText) {
        statusText.textContent = this.getStatusText(fileData.status)

        // Add error message if present
        if (fileData.status === 'error' && fileData.error) {
          statusText.innerHTML = `<span class="text-red-500 font-bold">Upload failed</span><br><span class="text-red-500">${fileData.error}</span>`
        }
      }

      if (progressBar) {
        progressBar.style.width = `${fileData.progress}%`
      }

      if (statusIcon) {
        statusIcon.innerHTML = this.getStatusIcon(fileData.status)
      }
    })
  }

  updateFilePreview = (fileData) => {
    if (!fileData.elements || !fileData.preview) return

    fileData.elements.forEach((element) => {
      const previewContainer = element.querySelector('[data-upload-image-container="true"]')
      if (!previewContainer) return

      if (fileData.preview.dataUrl) {
        previewContainer.innerHTML = `<img src="${fileData.preview.dataUrl}" style="width: 3rem; height: 3rem;" class="w-full h-full object-cover">`
      }
      else {
      // Use your logo for non-previewable files - you'll need to update this path
        previewContainer.innerHTML = `
          <div class="w-full h-full flex items-center justify-center bg-white rounded">
            <div class="text-xs font-medium text-gray-600">${fileData.preview.type}</div>
          </div>
        `
      }
    })
  }

  getStatusClasses = (status) => {
    const baseClasses = ''
    const statusClasses = {
      validating: 'opacity-75',
      pending: 'opacity-75',
      uploading: '',
      complete: '',
      error: 'opacity-50',
    }
    return baseClasses + ' ' + (statusClasses[status] || '')
  }

  getStatusText = (status) => {
    const statusMap = {
      validating: 'Validating',
      pending: 'Pending',
      uploading: 'Uploading',
      complete: 'Complete',
      error: 'Error',
    }
    return statusMap[status] || status
  }

  getStatusIcon = (status) => {
    switch (status) {
      case 'validating':
      case 'pending':
        return '<div class="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>'
      case 'uploading':
        return '<div class="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>'
      case 'complete':
        return '<svg class="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>'
      case 'error':
        return '<svg class="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg>'
      default:
        return ''
    }
  }

  openFileChooser = (event) => {
    event.preventDefault()
    this.element.click()
  }

  fileExists = (file) => {
    return this.files.some(f => (f.file.name === file.name && f.file.size === file.size))
  }

  uploadLimitReached = () => {
    return alerting(`Too many files! You have ${this.uploadLimitValue - this.activeUploads} uploads remaining!`)
  }

  alertTooManyFiles = () => {
    return alerting(`Too many files added at once, fewer than ${this.maxLengthValue} required!`)
  }

  checkForStalledUploads = () => {
    const now = new Date()

    this.files.forEach((fileData) => {
      if (fileData.status === 'uploading' && fileData.lastProgressUpdate) {
        const timeSinceUpdate = now - fileData.lastProgressUpdate

        if (timeSinceUpdate > UPLOAD_TIMEOUT) {
          if (fileData.uploader) {
            console.warn(`Aborting stalled upload for ${fileData.file.name}`)
            try {
              fileData.uploader.abort('Upload stalled')
            }
            catch (e) {
              console.error('Error aborting upload:', e)
            }
            this.handleFileError(fileData, new Error('Upload timed out after no activity for 2 minutes'))
          }
        }
      }
    })
  }

  async createProduct(fileData) {
    const uuid = fileData.uuid
    const filename = fileData.file.name
    const params = {
      print_product: {
        upload_info: {
          uuid: uuid,
          filename: filename,
          upload_status: 'uploading',
        },
      },
      thumbnail: fileData.preview?.dataUrl ?? null,
    }

    let response
    try {
      response = await post(URL, {
        responseKind: 'json',
        body: JSON.stringify(params),
      })
    }
    catch (error) {
      console.error('Error creating product:', error)
      throw new Error('Failed to create product record: network error')
    }

    if (response.ok) {
      return await response.json
    }

    // Surface the server's validation errors rather than a generic message
    let detail = `HTTP ${response.statusCode}`
    try {
      const body = await response.json
      if (body?.errors?.length) detail = body.errors.join(', ')
    }
    catch {
      // Non-JSON error page - keep the status code
    }
    throw new Error(`Failed to create product record (${detail})`)
  }

  formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  get uploadButton() {
    return document.getElementById('uploadNewProduct')
  }

  get mobileUploadButton() {
    return document.getElementById('mobileUploadNewProduct')
  }

  get dropOver() {
    return document.getElementById('dropOver')
  }

  get uploadsContainers() {
    return document.querySelectorAll('.print_products')
  }

  get fileOptions() {
    return {
      validType: this.validTypeValue,
      validSize: this.validSizeValue,
    }
  }
}
